<template>
	<div class="ventas_tab report_theme_container" data-theme="ventas">
		<!-- ================================================================ -->
		<!-- Header Section                                                    -->
		<!-- ================================================================ -->
		<div
			class="header_section_header is-flex is-flex-direction-row is-justify-content-space-between is-align-items-end mb-5">
			<div>
				<div class="header_title">Ventas</div>
				<div class="header_description mb-4">
					Analiza la tendencia y composición de tus ventas
				</div>

				<!-- Sub-Navigation: rango de períodos -->
				<div class="sub_nav_filters">
					<button v-for="filter in subNavFilters" :key="filter.id" class="filter_btn"
						:class="{ 'is-active': activeSubFilter === filter.id }"
						@click="updateActiveSubFilter(filter.id)">
						{{ filter.label }}
					</button>
				</div>
			</div>

			<!-- Right Actions -->
			<div class="right_actions is-flex">
				<div class="calendar_picker is-flex is-align-items-center mr-3">
					<AnualCalendar class="calendar" :value="endMonth" @onDateChange="updateEndMonth"
						:disableNewerDates="true" :disabledFrom="getNow" formatLabelDate="MMMM YYYY" :alignRight="true" />
				</div>
				<button class="theme_btn_white is-flex is-align-items-center">
					<DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
					Exportar
				</button>
			</div>
		</div>

		<!-- Summary Cards Section -->
		<SalesSummaryCards class="mt-5"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow" />

		<!-- Main Table Section -->
		<SalesEvolutionTable class="mt-6"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			@change-period="setPeriodRange"
			@retry="fetchSalesData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import AnualCalendar from "@/components/classification/AnualCalendar";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import SalesSummaryCards from "@/components/financialReports/SalesSummaryCards.vue";
import SalesEvolutionTable from "@/components/financialReports/SalesEvolutionTable.vue";
import { PERIOD_TYPES } from "../config/reportStructure";
import { capitalize } from "@/utils/string";

export default {
	name: "VentasTab",

	components: {
		AnualCalendar,
		DownloadFile,
		SalesSummaryCards,
		SalesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
		};
	},

	computed: {
		...mapState("classification", ["categories"]),
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("salesReport", [
			"activeSubFilter",
			"monthHeaders",
			"isLoading",
			"error",
		]),
		...mapGetters("salesReport", ["tableRows", "totalRow"]),

		getNow() {
			return moment().toDate();
		},

		subNavFilters() {
			const filters = [{ id: "cuentas", label: "Cuentas" }];
			this.categories.forEach(category => {
				filters.push({ id: category, label: capitalize(category) });
			});
			filters.push({ id: "clientes", label: "Clientes" });
			return filters;
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción.
		fetchSignature() {
			return [this.endMonth, this.selectedPeriodRange, this.activeSubFilter].join("|");
		},

		// Las summary cards solo dependen del mes de referencia: refetch
		// independiente para no perderlas al cambiar period range o subfilter.
		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("salesReport", ["fetchSalesData", "fetchSalesSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("salesReport", ["SET_ACTIVE_SUB_FILTER"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateActiveSubFilter(filter) {
			this.SET_ACTIVE_SUB_FILTER(filter);
		},
	},

	mounted() {
		this.fetchSalesData();
		this.fetchSalesSummary();
	},

	watch: {
		fetchSignature() { this.fetchSalesData(); },
		summarySignature() { this.fetchSalesSummary(); },
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.ventas_tab {
	position: relative;
	padding-bottom: 40px;
	// overflow-y: auto;
	height: 100%;
}

.header_title {
	font-size: 32px;
	font-weight: 700;
	color: var(--report-text);
	margin-bottom: 4px;
}

.header_description {
	font-size: 16px;
	color: var(--report-text-muted);
}

// ------------------------------------------------------------------
// Sub-nav filters (rango de período)
// ------------------------------------------------------------------

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: #4b5563;
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		color: #ffffff;
	}
}

// ------------------------------------------------------------------
// Right actions
// ------------------------------------------------------------------

.calendar_picker {
	background: var(--report-primary);
	color: white;
	border-radius: 8px;
	font-weight: 500;

	::v-deep .calendar-label {
		color: white !important;
	}
}

.theme_btn_white {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	color: #374151;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}
}
</style>
